import { Button, FileUploader, Form, Popup } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import React, { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { postApi, putApi } from '../../apiUtils';
import { useTranslation } from '../../contexts/translation';
import { AttachmentCategory } from '../../types/attachmentCategory.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import './AttachmentEditModal.scss';
import { uploadAttachment } from './uploadAttachment';

export const MAX_FILE_SIZE = 30000000;

export enum AttachmentKind {
  File = 'file',
  Hyperlink = 'hyperlink',
}

export interface AttachmentEditItem {
  /** klíč z gridu ve tvaru file_{id} nebo hyperlink_{id} */
  id: string;
  name: string;
  description: string;
  url: string;
  categoryId: number | null;
}

interface Props {
  /** Entita, ke které se příloha váže - Asset | AssetCycle | Task | MaintenanceCost */
  entityName: string;
  entityId: number;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Vyplněno = editace existující přílohy, prázdné = přidání nové */
  editItem?: AttachmentEditItem | null;
}

/**
 * Jednotný popup pro přidání a editaci přílohy (#737).
 * Nahrazuje původní dvojici panelů (FileUploader + HyperLinkForm) nad gridem.
 *
 * Přidání: přepínač soubor/odkaz, výběr souboru, název, kategorie. Vybírá se vždy jeden soubor,
 * po výběru se místo uploaderu zobrazí jeho název s možností výběr zrušit.
 * Kategorie se týká jen souborů, odkazy ji nemají.
 */
export const AttachmentEditModal = ({
  entityName,
  entityId,
  isVisible,
  onClose,
  onSuccess,
  editItem,
}: Props) => {
  const { translate } = useTranslation();
  const transFormName = 'File';
  const formRef = useRef<Form>(null);
  // vybírá se vždy jen jeden soubor - po výběru se místo uploaderu zobrazí jeho název
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const isEdit = !!editItem;
  const isEditedHyperlink = isEdit && editItem!.id.startsWith(`${AttachmentKind.Hyperlink}_`);

  const [formData] = useState<any>(() =>
    isEdit
      ? {
          kind: isEditedHyperlink ? AttachmentKind.Hyperlink : AttachmentKind.File,
          name: editItem!.name ?? '',
          description: editItem!.description ?? '',
          url: editItem!.url ?? '',
          categoryId: editItem!.categoryId ?? null,
        }
      : { kind: AttachmentKind.File, name: '', description: '', url: '', categoryId: null },
  );
  // kind je i ve stavu, aby se formulář překreslil při přepnutí soubor/odkaz
  const [kind, setKind] = useState<AttachmentKind>(formData.kind);
  const isFile = kind === AttachmentKind.File;

  // Kategorie pro danou entitu - API vrací jen ty, na které má uživatel roli
  const { data: categories } = useSWR<AttachmentCategory[]>(
    entityName ? `${API_URL.ATTACHMENT_CATEGORY_LIST_BY_ENTITY}?entity=${entityName}` : null,
    fetcher,
  );

  const categoryItems = useMemo(() => {
    const items = categories ? [...categories] : [];
    // nastavená kategorie nemusí být ve výběru (skrytá) - ať o ni uživatel editací nepřijde
    if (isEdit && editItem!.categoryId && !items.some((c) => c.id === editItem!.categoryId))
      items.push({
        id: editItem!.categoryId,
        entity: entityName,
        name: `#${editItem!.categoryId}`,
        requiredRole: null,
        sortOrder: 0,
        visible: false,
      });
    return items;
  }, [categories, editItem, entityName, isEdit]);

  const handleFileChanged = (e: any) => {
    const selected: File | null = e.value?.[0] ?? null;
    if (selected && selected.size > MAX_FILE_SIZE)
      notify(`${selected.name} - ${translate!('File is too large', '')}`, 'error');

    setFile(selected);
    // při prázdném názvu se předvyplní název souboru
    if (selected && !formRef.current?.instance.option('formData').name)
      formRef.current?.instance.updateData('name', selected.name);
  };

  /** Zrušení vybraného souboru. Odmountováním uploaderu se vyprázdní i jeho vnitřní hodnota,
   * takže jde znovu vybrat ten samý soubor. */
  const handleFileRemoved = () => {
    // předvyplněný název už nemá k čemu patřit
    if (formRef.current?.instance.option('formData').name === file?.name)
      formRef.current?.instance.updateData('name', '');
    setFile(null);
  };

  const uploadFile = async () =>
    uploadAttachment(entityName, entityId, file!, {
      name: formData.name,
      description: formData.description,
      categoryId: formData.categoryId,
    });

  const createHyperlink = async () => {
    let isOk = false;
    await postApi({
      url: API_URL.HYPERLINK_CREATE,
      data: { name: formData.name, uri: formData.url, entity: entityName, entityId },
      callAfterSuccess: () => {
        isOk = true;
      },
    });
    return isOk;
  };

  const updateAttachment = async () => {
    const id = Number(editItem!.id.split('_')[1]);
    let isOk = false;
    await putApi({
      url: `${API_URL.FILE_PUT}?entity=${entityName}&entityId=${entityId}`,
      data: {
        id,
        name: formData.name,
        description: formData.description,
        // 0 = kategorii zrušit, null by na BE znamenalo "neměnit"
        attachmentCategoryId: formData.categoryId ?? 0,
      },
      callAfterSuccess: () => {
        isOk = true;
      },
    });
    return isOk;
  };

  const updateHyperlink = async () => {
    const id = Number(editItem!.id.split('_')[1]);
    let isOk = false;
    await putApi({
      url: API_URL.HYPERLINK_UPDATE,
      data: { id, entity: entityName, entityId, name: formData.name, uri: formData.url },
      callAfterSuccess: () => {
        isOk = true;
      },
    });
    return isOk;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!formRef.current?.instance.validate().isValid) return;

    if (!isEdit && isFile && !file) {
      notify(translate!('Select files', transFormName), 'error');
      return;
    }

    setSaving(true);
    let isOk = false;
    if (isEdit) isOk = isEditedHyperlink ? await updateHyperlink() : await updateAttachment();
    else if (isFile) isOk = !!(await uploadFile());
    else isOk = await createHyperlink();
    setSaving(false);

    if (!isOk) return;
    onSuccess();
    onClose();
  };

  return (
    <Popup
      visible={isVisible}
      dragEnabled={false}
      closeOnOutsideClick={false}
      onHiding={onClose}
      showTitle
      title={translate!(isEdit ? 'Edit attachment' : 'Add attachment', transFormName)}
      width={'auto'}
      height={'auto'}
    >
      <form onSubmit={handleSubmit}>
        <Form formData={formData} showColonAfterLabel showValidationSummary ref={formRef}>
          <GroupItem visible={!isEdit}>
            <SimpleItem
              dataField="kind"
              editorType="dxRadioGroup"
              editorOptions={{
                layout: 'horizontal',
                valueExpr: 'id',
                displayExpr: 'name',
                items: [
                  { id: AttachmentKind.File, name: translate!('File', transFormName) },
                  { id: AttachmentKind.Hyperlink, name: translate!('Hyperlink', transFormName) },
                ],
                onValueChanged: (e: any) => setKind(e.value),
              }}
            >
              <Label text={translate!('Type', transFormName)} />
            </SimpleItem>
          </GroupItem>

          <GroupItem visible={isFile && !isEdit}>
            <SimpleItem>
              <Label text={translate!('File upload', transFormName)} />
              {file ? (
                <div className="attachment-selected-file">
                  <span className="name">{file.name}</span>
                  <Button
                    icon="close"
                    stylingMode="text"
                    hint={translate!('Cancel', '')}
                    onClick={handleFileRemoved}
                  />
                </div>
              ) : (
                /* useButtons, ne useForm - v useForm si drop obsluhuje nativní input[type=file],
                   který přijme jen soubor z disku. V useButtons ho odbaví DevExtreme v JS přes
                   dataTransfer.files, takže projde i obrázek přetažený ze stránky v prohlížeči.
                   Vlastní upload se nespouští, ten řešíme až při uložení formuláře. */
                <div className="attachment-file-upload">
                  <FileUploader
                    name={'files'}
                    multiple={false}
                    accept={'*'}
                    uploadMode={'useButtons'}
                    selectButtonText={translate!('Select files', transFormName)}
                    labelText={translate!('or drop files here', transFormName)}
                    onValueChanged={handleFileChanged}
                    showFileList={false}
                    width={360}
                    maxFileSize={MAX_FILE_SIZE}
                  />
                </div>
              )}
            </SimpleItem>
          </GroupItem>

          <GroupItem>
            <SimpleItem dataField="name" editorType="dxTextBox" editorOptions={{ width: 360 }}>
              <Label text={translate!('Name', transFormName)} />
              {/* u souboru se název předvyplní z názvu souboru, proto je povinný jen u odkazu */}
              {!isFile && <RequiredRule message={translate!('Name is required', transFormName)} />}
            </SimpleItem>
          </GroupItem>

          <GroupItem visible={isFile}>
            <SimpleItem
              dataField="description"
              editorType="dxTextBox"
              editorOptions={{ width: 360 }}
            >
              <Label text={translate!('Description', transFormName)} />
            </SimpleItem>
          </GroupItem>

          {/* kategorie se týká jen souborů, hyperlinky ji nemají */}
          <GroupItem visible={isFile}>
            <SimpleItem
              dataField="categoryId"
              editorType="dxSelectBox"
              editorOptions={{
                items: categoryItems,
                valueExpr: 'id',
                displayExpr: 'name',
                showClearButton: true,
                width: 360,
              }}
            >
              <Label text={translate!('Category', transFormName)} />
            </SimpleItem>
          </GroupItem>

          <GroupItem visible={!isFile}>
            <SimpleItem dataField="url" editorType="dxTextBox" editorOptions={{ width: 360 }}>
              <Label text={translate!('URL', transFormName)} />
              {!isFile && <RequiredRule message={translate!('URL is required', transFormName)} />}
            </SimpleItem>
          </GroupItem>

          <GroupItem colCount={4}>
            <GroupItem colSpan={2}></GroupItem>
            <ButtonItem>
              <ButtonOptions
                width={'100%'}
                type={'success'}
                text={translate!('Save', '')}
                disabled={saving}
                useSubmitBehavior
              />
            </ButtonItem>
            <ButtonItem>
              <ButtonOptions
                width={'100%'}
                type={'normal'}
                text={translate!('Close', '')}
                onClick={onClose}
              />
            </ButtonItem>
          </GroupItem>
        </Form>
      </form>
    </Popup>
  );
};
